import {SortDirection} from '@angular/material/sort';

export const CASES_OVERVIEW_STORAGE_KEY = 'casesOverview.preferences.v2';
export const CASES_OVERVIEW_STATE_FILTER_OPTIONS: string[] = ['UNASSIGNED', 'DRAFT', 'PENDING', 'REVIEW', 'READY', 'PROCESSING', 'AUTOMATED', 'COMPLETED'];

/**
 * The option list before PROCESSING was added. A stored selection that matches it exactly used to mean "everything",
 * so it is upgraded to "no filter" instead of silently hiding the new state from every returning user.
 */
const STATE_FILTER_OPTIONS_BEFORE_PROCESSING: string[] =
  ['UNASSIGNED', 'DRAFT', 'PENDING', 'REVIEW', 'READY', 'AUTOMATED', 'COMPLETED'];

/** The states the filter offers to a tenant that runs the processing phase. */
export function stateFilterOptionsWithProcessing(): string[] {
  return CASES_OVERVIEW_STATE_FILTER_OPTIONS;
}

/**
 * The states the filter offers to a tenant without the processing phase. PROCESSING is dropped because it would
 * filter for a state no case of that tenant can ever reach.
 */
export function stateFilterOptionsWithoutProcessing(): string[] {
  return CASES_OVERVIEW_STATE_FILTER_OPTIONS.filter(state => state !== 'PROCESSING');
}

function isLegacyFullSelection(selectedStates: string[]): boolean {
  return selectedStates.length === STATE_FILTER_OPTIONS_BEFORE_PROCESSING.length
    && STATE_FILTER_OPTIONS_BEFORE_PROCESSING.every(state => selectedStates.includes(state));
}

export interface CasesOverviewPreferences {
  searchTerm: string;
  onlyShowMyCases: boolean;
  sortActive: string | null;
  sortDirection: SortDirection | null;
  stateFilters: string[] | null;
}

export function sanitizePreferences(value: unknown): CasesOverviewPreferences | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const raw = value as Partial<CasesOverviewPreferences>;
  const searchTerm = typeof raw.searchTerm === 'string' ? raw.searchTerm : '';
  const onlyShowMyCases = typeof raw.onlyShowMyCases === 'boolean' ? raw.onlyShowMyCases : false;
  const sortActive = typeof raw.sortActive === 'string' ? raw.sortActive : null;
  const sortDirection = raw.sortDirection === 'asc' || raw.sortDirection === 'desc' ? raw.sortDirection : null;
  const filteredStates = Array.isArray(raw.stateFilters)
    ? raw.stateFilters.filter(
        (state): state is string =>
          CASES_OVERVIEW_STATE_FILTER_OPTIONS.includes(state)
      )
    : null;
  const stateFilters = filteredStates && filteredStates.length > 0 && !isLegacyFullSelection(filteredStates)
    ? filteredStates
    : null;

  return {
    searchTerm,
    onlyShowMyCases,
    sortActive,
    sortDirection,
    stateFilters
  };
}

export function loadPreferences(): CasesOverviewPreferences | null {
  try {
    const rawPreferencesString = localStorage.getItem(CASES_OVERVIEW_STORAGE_KEY);
    if (!rawPreferencesString) {
      return null;
    }
    const parsed = JSON.parse(rawPreferencesString);
    return sanitizePreferences(parsed);
  } catch (error) {
    console.warn('Failed to load cases overview preferences from localStorage.', error);
    removePreferences();
    return null;
  }
}

export function removePreferences(): void {
  localStorage.removeItem(CASES_OVERVIEW_STORAGE_KEY);
}

export interface CasesUrlParams {
  baseUrl: string;
  page: number;
  size: number;
  sortActive: string | null;
  sortDirection: SortDirection | null;
  searchTerm: string | null;
  userFilter: string | null;
  stateFilters: string[] | null;
  availableStates?: string[];
}

export function buildCasesUrl(params: CasesUrlParams): URL {
  const url = new URL(params.baseUrl, globalThis.location.origin);
  url.searchParams.set('size', params.size.toString());
  url.searchParams.set('page', params.page.toString());

  const sortActive = params.sortActive ?? 'createdDate';
  const sortDirection = params.sortDirection ?? 'desc';
  url.searchParams.set('sort', `${sortActive},${sortDirection}`);

  if (params.searchTerm && params.searchTerm.length > 0) {
    url.searchParams.set('searchParameter', params.searchTerm);
  } else {
    url.searchParams.delete('searchParameter');
  }

  if (params.userFilter) {
    url.searchParams.set('userFilter', params.userFilter);
  } else {
    url.searchParams.delete('userFilter');
  }

  if (params.stateFilters) {
    applyStateFilterToUrl(url, params.stateFilters, params.availableStates);
  }

  return url;
}

export function applyStateFilterToUrl(url: URL, selectedStates: string[],
                                      availableStates: string[] = CASES_OVERVIEW_STATE_FILTER_OPTIONS): void {
  if (selectedStates.length === availableStates.length) {
    url.searchParams.delete('stateFilter');
    return;
  }
  if (selectedStates.length === 0) {
    url.searchParams.set('stateFilter', '');
    return;
  }
  url.searchParams.set('stateFilter', selectedStates.join(','));
}

